import _ from 'lodash';
import React from 'react';
import { Lang } from 'shared/constants';
import { Input, Loader } from '../../../components/core';
import styles from './owners-management.module.scss';
import { Table } from 'antd';
import { removeDiacritics, normalizedContains, sortByAlphabet } from 'shared/utils';
import { DeleteIcon, Pencil } from '../../../images';
import { OwnerEditor } from '../../../components/ownerSelector/owner-editor';
import { ActionButton } from '../../../components/common';
import { OwnerDelete } from '../../../components/owners/owners-delete/owner-delete';
import { useCallback } from 'react';

const OWNER_ENTITY_DEFAULT = { id: '', firstName: '', lastName: '', email: '', phone: '' };

const OwnersManagementPageComponent = ({ lang, owners }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [editEntity, setEditEntity] = React.useState(null);
  const [deleteEntity, setDeleteEntity] = React.useState(null);

  const closeCreateOwner = () => setEditEntity(null);
  const closeDeleteOwner = () => setDeleteEntity(null);

  const onSearchTermChanged = e => setSearchTerm(removeDiacritics(e?.target?.value));

  const onCreateOwner = useCallback(() => {
    setEditEntity(OWNER_ENTITY_DEFAULT);
  }, [setEditEntity]);

  const columns = [
    {
      title: lang.USERS_EMAIL,
      dataIndex: 'email',
      sorter: {
        compare: (a, b) => sortByAlphabet(a.email, b.email),
      },
    },

    {
      title: lang.FIRST_NAME,
      dataIndex: 'firstName',
      sorter: {
        compare: (a, b) => sortByAlphabet(a.firstName, b.firstName),
      },
    },
    {
      title: lang.LAST_NAME,
      dataIndex: 'lastName',
      sorter: {
        compare: (a, b) => sortByAlphabet(a.lastName, b.lastName),
      },
      defaultSortOrder: 'ascend',
    },
    {
      title: lang.PHONE,
      dataIndex: 'phone',
    },
    {
      title: lang.ACTIONS,
      width: 80,
      render: owner => (
        <div className={styles.ownerActions}>
          <Pencil className={styles.editIcon} onClick={() => setEditEntity(owner)} />
          <DeleteIcon className={styles.deleteIcon} onClick={() => setDeleteEntity(owner)} />
        </div>
      ),
    },
  ];

  const filterAction = ({ email, firstName, lastName }) =>
    normalizedContains(email, searchTerm) ||
    normalizedContains(firstName, searchTerm) ||
    normalizedContains(lastName, searchTerm);

  const renderList = React.useCallback(() => {
    if (_.isNull(owners)) {
      return <Loader />;
    }

    return (
      <Table
        pagination={{
          showSizeChanger: true,
        }}
        defaultSortField="lastName"
        columns={columns}
        dataSource={owners
          .filter(filterAction)
          .map(owner => ({ ...owner, key: owner.id, phone: owner.phone ?? '' }))}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owners, searchTerm]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2>{lang.HEADER}</h2>
        <Input
          className={styles.input}
          onChange={onSearchTermChanged}
          placeholder={lang.SEARCH_PLACEHOLDER}
        />
      </div>

      {renderList()}

      {editEntity && <OwnerEditor isOpen={true} entity={editEntity} onFinish={closeCreateOwner} />}

      {deleteEntity && (
        <OwnerDelete isOpen={true} entity={deleteEntity} onFinish={closeDeleteOwner} />
      )}

      <ActionButton onClick={onCreateOwner} labelText={lang.ADD_NEW_OWNER} />
    </div>
  );
};

OwnersManagementPageComponent.defaultProps = {
  lang: Lang.OWNERS_MANAGEMENT,
  owners: null,
};

export { OwnersManagementPageComponent };
